import { useMemo, useState } from 'react'
import { ChatUser } from '../types'
import { ChannelFilters } from 'stream-chat'

type UseChatFiltersOptions = {
  includeEmptyChannels?: boolean
}

export const useChatFilters = (user: ChatUser, options: UseChatFiltersOptions = {}) => {
  const { includeEmptyChannels = false } = options
  const [searchText, setSearchText] = useState('')

  const filters: ChannelFilters = useMemo(() => {
    // Stream's `ChannelFilters` TS type does not include `$ne` in the operator
    // union (`{ $eq?, $exists?, $gt?, $gte?, $in?, $lt?, $lte? }`), but the
    // Stream backend does accept it at runtime. Casting the literal to
    // `ChannelFilters` keeps the rest of the type checking intact while
    // bypassing this single missing-operator gap.
    const baseFilter: ChannelFilters = includeEmptyChannels
      ? { members: { $in: [user?.id] } }
      : ({
          members: { $in: [user?.id] },
          $or: [
            { type: { $ne: 'messaging' } },
            {
              type: 'messaging',
              last_message_at: { $exists: true },
            }
          ]
        } as unknown as ChannelFilters)

    const search = searchText?.trim?.()

    if (search) {
      const searchKey = `search_${user?.id}`
      return {
        $and: [
          baseFilter,
          {
            $or: [
              {
                type: 'messaging',
                [searchKey]: { $autocomplete: search }
              },
              {
                type: 'team',
                name: { $autocomplete: search }
              },
            ]
          }
        ]
      }
    }

    return baseFilter
  }, [searchText, user?.id, includeEmptyChannels])

  return {
    filters,
    searchText,
    setSearchText,
  }
}
